/**
 * Shared primitives ported from the Qwik build: Button, HighlightedButton,
 * Modal, Toast, StatusLabel, CampaignLegend, spinners, Dropzone, ErrorMessage.
 * Markup and classes are kept identical so legacy.css applies unchanged.
 */

import {
  type ButtonHTMLAttributes,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { CheckIcon, XCircleIcon, XMarkIcon } from "../../icons/index.tsx";
import type { LegacyStatus } from "../../lib/legacy.ts";
import { STATUS_ORDER } from "../../lib/legacy.ts";
import {
  CampaignStatusBadge,
  FinalPdfBadge,
  StatusLegend,
  StatusLegendToggle,
} from "../status.tsx";

// ---------------------------------------------------------------- buttons

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { extraClass?: string };

export function Button({ extraClass = "", className, children, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={`flex items-center justify-around bg-gray-100 border rounded-md py-2 px-4 text-base font-medium text-black hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-gray-800 ${extraClass} ${className ?? ""}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function HighlightedButton({
  extraClass = "",
  disabled = false,
  danger = false,
  children,
  ...rest
}: ButtonProps & { danger?: boolean }) {
  const color = danger
    ? "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
    : "bg-primary-500 hover:bg-primary-600 focus:ring-gray-800";
  return (
    <button
      type="button"
      disabled={disabled}
      className={`${disabled ? "cursor-not-allowed" : ""} flex items-center justify-around ${color} border border-transparent rounded-md py-2 px-4 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-0 ${extraClass}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ modal

interface ModalProps {
  title: ReactNode;
  open: boolean;
  iconBackground?: string;
  disableSubmitButton?: boolean;
  submitText?: string;
  cancelText?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  icon?: ReactNode;
  children?: ReactNode;
  /** Narrow panel, white footer, and labels with no button icons. */
  compact?: boolean;
  /** Tailwind max-width class. Defaults to sm:max-w-md when compact, else sm:max-w-3xl. */
  width?: string;
  /** Rose submit button. extraClass cannot override the primary fill. */
  danger?: boolean;
  /** Footer control between Cancel and the submit button. */
  middle?: ReactNode;
  /** Pin Cancel to the left; middle and submit stay on the right. */
  splitFooter?: boolean;
}

export function Modal({
  title,
  open,
  iconBackground,
  disableSubmitButton,
  submitText,
  cancelText,
  onSubmit,
  onCancel,
  onClose,
  icon,
  children,
  compact,
  danger,
  middle,
  splitFooter,
  width,
}: ModalProps) {
  const cancelButton = onCancel && (
    <Button onClick={onCancel}>
      {compact ? null : <XMarkIcon />} {cancelText ?? "Cancel"}
    </Button>
  );
  const closeButton = onClose && (
    <Button onClick={onClose}>{compact ? null : <XMarkIcon />} Close</Button>
  );
  const submitButton = onSubmit && (
    <HighlightedButton onClick={onSubmit} disabled={disableSubmitButton} danger={danger}>
      {compact ? null : <CheckIcon />} {submitText ? submitText : "Submit"}
    </HighlightedButton>
  );
  return (
    <div
      className={`relative z-[100] ${open ? "" : "hidden"}`}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
          <div
            className={`relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform sm:my-8 sm:w-full ${
              width ?? (compact ? "sm:max-w-md" : "sm:max-w-3xl")
            }`}
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className={`modal-content ${icon ? "has-icon" : "sm:flex sm:items-start"}`}>
                <div
                  className={`modal-icon mx-auto flex-shrink-0 items-center justify-center h-12 w-12 rounded-full ${
                    icon ? "!mx-0 !flex" : ""
                  } ${iconBackground ? iconBackground : "bg-white"} sm:mx-0 sm:h-10 sm:w-10`}
                >
                  {icon}
                </div>
                <div
                  className={`mt-3 text-center sm:mt-0 sm:text-left w-full ${icon ? "!mt-0 min-w-0 flex-1 !text-left" : ""}`}
                >
                  <h3 className="text-xl leading-6 font-medium text-gray-900" id="modal-title">
                    {title}
                  </h3>
                  <div className="mt-2">
                    <div className="text-base text-gray-500">{children}</div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`${compact ? "bg-white" : "sm:bg-gray-100"} px-4 pb-3 sm:pt-3 sm:px-6 sm:flex space-y-2 sm:space-y-0 ${
                splitFooter ? "sm:items-center sm:justify-between" : "justify-end sm:gap-x-4"
              }`}
            >
              {splitFooter ? <div>{cancelButton}</div> : cancelButton}
              {splitFooter ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-x-4">
                  {closeButton}
                  {middle}
                  {submitButton}
                </div>
              ) : (
                <>
                  {closeButton}
                  {middle}
                  {submitButton}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ toast

interface ToastState {
  message: string;
  type: string;
  visible: boolean;
}

interface ToastApi {
  toast: ToastState;
  showToast: (message: string, type?: string) => void;
  dismissToast: () => void;
}

const ToastContext = createContext<ToastApi>({
  toast: { message: "", type: "success", visible: false },
  showToast: () => {},
  dismissToast: () => {},
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>({ message: "", type: "success", visible: false });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissToast = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setToast((current) => ({ ...current, visible: false }));
  }, []);
  const showToast = useCallback((message: string, type = "success") => {
    setToast({ message, type, visible: true });
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    // Errors stay until dismissed. Everything else fades after 3 seconds.
    if (type === "error") return;
    timer.current = setTimeout(() => setToast((current) => ({ ...current, visible: false })), 3000);
  }, []);
  const value = useMemo(
    () => ({ toast, showToast, dismissToast }),
    [toast, showToast, dismissToast],
  );
  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast />
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/** daisyUI 3 has alert-success/warning/info; anything else falls back to plain. Errors are a floating panel. */
export function Toast() {
  const { toast, dismissToast } = useToast();
  const shown = toast.visible ? "opacity-100" : "opacity-0 pointer-events-none";
  if (toast.type === "error") {
    return (
      <div
        className={`fixed inset-x-0 top-4 z-[200] flex justify-center px-4 transition-opacity duration-300 ${shown}`}
        role="alert"
      >
        <div className="flex w-full max-w-lg items-start justify-between gap-4 rounded-lg bg-red-700 px-5 py-4 text-white shadow-lg">
          <p className="text-base leading-6">{toast.message}</p>
          <button type="button" className="shrink-0" aria-label="Close" onClick={dismissToast}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={`fixed bottom-4 right-4 z-[200] transition-opacity duration-300 ${shown}`}>
      <div className={`alert alert-${toast.type}`}>{toast.message}</div>
    </div>
  );
}

// ----------------------------------------------------------- status label

interface StatusLabelProps {
  statusLabel: LegacyStatus;
  campaignStatus: string;
  adminMode: boolean;
  showStatusPills: boolean;
  onToggle: () => void;
  finalPdfStatus?: string | null;
  finalPdfUpdatedAt?: number | null;
}

/** One pill. Kept for callers that pass the seven statuses; only the match renders. */
export function StatusLabel({
  statusLabel,
  campaignStatus,
  adminMode,
  showStatusPills,
  onToggle,
  finalPdfStatus,
  finalPdfUpdatedAt,
}: StatusLabelProps) {
  if (campaignStatus !== statusLabel) return null;
  let buttonText: string = statusLabel;
  if (statusLabel === "Complete") buttonText = "Approved";
  if (adminMode && statusLabel === "Out On Proof") buttonText = "Approve me";
  return (
    <span className="inline-flex items-center gap-2">
      <CampaignStatusBadge
        status={statusLabel}
        label={buttonText}
        showLabel={showStatusPills}
        onToggle={onToggle}
      />
      <FinalPdfBadge status={finalPdfStatus} updatedAt={finalPdfUpdatedAt} />
    </span>
  );
}

/** All seven pills for a row; only the matching one renders. */
export function StatusPills(props: Omit<StatusLabelProps, "statusLabel">) {
  return (
    <>
      {STATUS_ORDER.map((s) => (
        <StatusLabel key={s} statusLabel={s} {...props} />
      ))}
    </>
  );
}

// ----------------------------------------------------------------- legend

export { StatusLegendToggle as CampaignLegendToggle };

export function CampaignLegend(props: {
  show: boolean;
  adminMode: boolean;
  todaysDate: string;
  showLabels: boolean;
  onToggleLabels: () => void;
}) {
  return <StatusLegend {...props} />;
}

// --------------------------------------------------------------- spinners

export function LoadingSpinner() {
  return (
    <div className="loading-container">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="loading-spinner-econnect"
        width="32"
        height="32"
        viewBox="0 0 24 24"
      >
        <path d="M2,12A11.2,11.2,0,0,1,13,1.05C12.67,1,12.34,1,12,1a11,11,0,0,0,0,22c.34,0,.67,0,1-.05C6,23,2,17.74,2,12Z">
          <animateTransform
            attributeName="transform"
            dur="0.6s"
            repeatCount="indefinite"
            type="rotate"
            values="0 12 12;360 12 12"
          />
        </path>
      </svg>
      <p className="loading-text">Loading…</p>
    </div>
  );
}

export function LoadingSpinnerWithBackground({
  message = "Loading...",
  progress,
}: {
  message?: string;
  progress?: number;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-50" />
      <div className="relative z-10 min-w-[200px] rounded-lg p-6">
        <div className="flex flex-col items-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-t-transparent" />
          <div className="mt-4 text-center">
            <span className="text-sm font-medium text-white">{message}</span>
            {progress !== undefined && (
              <div className="mt-2">
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="mt-1 text-xs text-gray-500">{progress}%</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- dropzone

interface DropzoneProps {
  fileUploadText?: string;
  fileTypeMessage?: string;
  /** MIME types accepted, e.g. ["application/pdf"]. */
  fileTypes?: string[];
  forceClass?: "" | "warning" | "danger";
  uploadFile?: (file: File) => void;
  /** Card border for the address upload. The artwork modal keeps the legacy skin. */
  variant?: "legacy" | "card";
}

/**
 * The legacy Dropzone was react-dropzone wrapped in qwikify$. The same
 * markup with native drag events is 40 lines, so no dependency.
 */
export function Dropzone({
  fileUploadText = "Upload your Artwork files",
  fileTypeMessage = "PDF, PNG or JPEG files accepted. For best results ensure file is at least 300dpi.",
  fileTypes = ["image/jpeg", "image/png", "application/pdf"],
  forceClass = "",
  uploadFile,
  variant = "legacy",
}: DropzoneProps) {
  const input = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [accepted, setAccepted] = useState<File[]>([]);
  const [rejected, setRejected] = useState<File[]>([]);

  const accepts = (file: File) => {
    if (fileTypes.length === 0) return true;
    if (fileTypes.includes(file.type)) return true;
    // Browsers report CSVs inconsistently (text/csv, application/vnd.ms-excel, "").
    return fileTypes.includes("text/csv") && /\.csv$/i.test(file.name);
  };

  const take = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const ok: File[] = [];
    const bad: File[] = [];
    for (const f of Array.from(files)) (accepts(f) ? ok : bad).push(f);
    setAccepted(ok);
    setRejected(bad);
    if (ok[0] && uploadFile) uploadFile(ok[0]);
  };

  const card = variant === "card";
  return (
    <section className={card ? "" : "container"}>
      <div
        className={
          card
            ? `flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-5 py-8 text-center text-sm ${
                active
                  ? "border-primary-400 bg-primary-50"
                  : forceClass === "danger"
                    ? "border-rose-300 bg-white hover:bg-rose-50"
                    : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
              }`
            : `dropzone ${active ? "dropzone-hover" : ""} ${forceClass}`
        }
        onClick={() => input.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setActive(false);
          take(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
      >
        <input
          ref={input}
          type="file"
          accept={fileTypes.join(",")}
          style={{ display: "none" }}
          onChange={(e) => take(e.target.files)}
        />
        {card ? (
          <>
            <p className="font-medium text-gray-900">
              {active ? "Drop the file" : "Drag and drop, or click to upload"}
            </p>
            <p className="mt-1 text-gray-500">
              <span
                className={
                  forceClass === "danger"
                    ? "font-medium text-rose-700"
                    : "font-medium text-gray-700"
                }
              >
                {fileUploadText}
              </span>{" "}
              {fileTypeMessage}
            </p>
          </>
        ) : active ? (
          <p className="text-green-700 font-bold">"Drop files now!"</p>
        ) : (
          <p>
            <span className="font-bold text-sky-600">Drag &amp; Drop</span> or Click to upload!
          </p>
        )}
        {!card && (
          <em>
            <span className={`dropzone-text ${forceClass}`}>{fileUploadText}</span>{" "}
            {fileTypeMessage}
          </em>
        )}
      </div>
      <div className={card ? "mt-3 text-sm text-gray-600" : "dropzone-results"}>
        {accepted.length > 0 && (
          <>
            <h4 className="dropzone-heading">Accepted files</h4>
            <div className="file-pills">
              {accepted.map((file) => (
                <span key={file.name} className="file-pill">
                  <span>{file.name}</span>
                  <span className="file-size">{(file.size / 1000000).toFixed(2)}MB</span>
                </span>
              ))}
            </div>
          </>
        )}
        {rejected.length > 0 && (
          <>
            <h4 className="dropzone-heading rejected">Rejected files</h4>
            <ul>
              {rejected.map((file) => (
                <li key={file.name}>
                  <span>{file.name}</span>
                  <span className="file-size">{(file.size / 1000000).toFixed(2)}MB</span>
                  <ul>
                    <li className="file-error">File type must be one of {fileTypes.join(", ")}</li>
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------------------ error block

export function ErrorMessage({ heading, message }: { heading: string; message: string }) {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircleIcon />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">{heading}</h3>
          <p className="text-sm text-red-700 mt-2">{message}</p>
        </div>
      </div>
    </div>
  );
}

/** The daisyUI alert with the warning triangle the legacy notices used. */
export function AlertIcon({ kind }: { kind: "warning" | "info" }) {
  return kind === "warning" ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="stroke-current shrink-0 h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      className="stroke-current shrink-0 w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
